// De privacyverklaring, in het spel zelf.
//
// Eén bron voor twee plekken: het paneel dat je vanuit de lobby opent, en de
// losse pagina op /privacy die je in het Developer Portal kunt invullen. Die
// laatste werkt buiten Discord om, want daar klikt Discord hem open.

import type { Lang } from './i18n'

export interface PrivacySection {
  title: string
  body: string[]
}

export interface PrivacyDoc {
  title: string
  updated: string
  intro: string[]
  sections: PrivacySection[]
}

const nl: PrivacyDoc = {
  title: 'Privacy',
  updated: 'Bijgewerkt op 6 september 2026',
  intro: [
    'Treehouse Quiz is een woordraadspel dat je in een Discord-spraakkanaal speelt. Hieronder staat wat het spel met je gegevens doet.',
    'Dit gaat alleen over het spel zelf. Wat Discord doet staat in de privacyverklaring van Discord. Die vervangt deze niet, en andersom ook niet.',
  ],
  sections: [
    {
      title: 'Wat het spel van je ziet',
      body: [
        'Je Discord-naam en avatar, zodat je in het juiste team te zien bent.',
        'In welke server en welk spraakkanaal je speelt, zodat iedereen in hetzelfde potje komt.',
        'Wat je in het spel doet: je team, de woorden die afgevinkt worden, de stand.',
        'Geen e-mailadres, geen berichten, en niets uit andere kanalen.',
      ],
    },
    {
      title: 'Meeluisteren',
      body: [
        'Staat meeluisteren aan, dan komt er een bot in het spraakkanaal die herkent wanneer een woord goed geraden wordt.',
        'Alleen tijdens een ronde. In de lobby, tussen rondes door en na afloop staan de microfoons dicht. De balk onderin laat zien wanneer ze open staan.',
        'Alleen naar wie op dat moment aan het raden is. De omschrijver hoort daar niet bij.',
        'Het geluid gaat rechtstreeks naar Soniox, wordt daar omgezet naar tekst en is daarna weg. Er wordt niets opgenomen en niets bewaard.',
        'De bot staat gedempt en zegt zelf nooit iets.',
        'De end-to-end versleuteling van Discord blijft aan. De bot doet mee als deelnemer, niet als iets ertussen.',
      ],
    },
    {
      title: 'Liever niet',
      body: [
        'De host kan meeluisteren uitzetten in de lobby. Dan komt de bot het spraakkanaal niet eens in en vinkt de omschrijver de woorden zelf af. Het spel werkt verder precies hetzelfde.',
      ],
    },
    {
      title: 'Wat er bewaard wordt',
      body: [
        'Niets blijvends. Geen spelersdatabase, geen geschiedenis van potjes, geen opgeslagen geluid. Alles staat in het geheugen zolang de pot loopt en is daarna weg.',
        'Op de server staan technische logboeken om storingen op te lossen. Daar zit geen geluid in en ze worden regelmatig opgeruimd.',
      ],
    },
    {
      title: 'Jongere spelers',
      body: [
        'Op deze server spelen ook jongeren mee. Daarom vraagt het spel niets wat het niet nodig heeft, bewaart het geen geluid, en kan meeluisteren altijd uit.',
      ],
    },
  ],
}

const en: PrivacyDoc = {
  title: 'Privacy',
  updated: 'Updated 6 September 2026',
  intro: [
    'Treehouse Quiz is a word guessing game you play in a Discord voice channel. Here is what the game does with your data.',
    'This covers the game only. What Discord does is in their own privacy policy. Neither replaces the other.',
  ],
  sections: [
    {
      title: 'What the game sees',
      body: [
        'Your Discord name and avatar, so you show up in the right team.',
        'Which server and voice channel you are in, so everyone ends up in the same game.',
        'What you do in the game: your team, the words ticked off, the score.',
        'No email address, no messages, nothing from other channels.',
      ],
    },
    {
      title: 'Listening in',
      body: [
        'With listening on, a bot joins the voice channel and recognises when a word is guessed correctly.',
        'Only during a round. In the lobby, between turns and afterwards the microphones are closed. The bar at the bottom shows when they are open.',
        'Only to whoever is guessing at that moment. The describer is never included.',
        'Audio goes straight to Soniox, is turned into text there, and is gone afterwards. Nothing is recorded and nothing is kept.',
        'The bot is muted and never says anything itself.',
        "Discord's end-to-end encryption stays on. The bot takes part as a participant, not as something in between.",
      ],
    },
    {
      title: 'Rather not',
      body: [
        'The host can switch listening off in the lobby. The bot then stays out of the voice channel and the describer ticks the words off. The game plays exactly the same.',
      ],
    },
    {
      title: 'What is stored',
      body: [
        'Nothing lasting. No player database, no game history, no stored audio. Everything lives in memory while the game runs and is gone afterwards.',
        'The server keeps technical logs for troubleshooting. They contain no audio and are cleared regularly.',
      ],
    },
    {
      title: 'Younger players',
      body: [
        'Younger people play on this server too. That is why the game asks for nothing it does not need, keeps no audio, and lets you turn listening off at any time.',
      ],
    },
  ],
}

export const PRIVACY: Record<Lang, PrivacyDoc> = { nl, en }

/** Discord opent deze pagina buiten de client om, dus die moet los werken. */
export function isPrivacyRoute(): boolean {
  const pad = location.pathname.replace(/\/+$/, '')
  return pad.endsWith('/privacy') || new URLSearchParams(location.search).has('privacy')
}
